import { Box, Container, IconButton, Fade, Spinner } from "@chakra-ui/react";
import { ChevronUpIcon } from "@chakra-ui/icons";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import About from "./About";
import Contact from "./Contact";
import Events from "./Events";
import Footer from "../components/Footer";
import Team from "./Team";
import { useEffect, useState } from "react";

// Utility hook: περιμένει να φορτώσουν όλες οι εικόνες
function useImagesLoaded(imagePaths = []) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!imagePaths.length) {
      setLoaded(true);
      return;
    }
    let count = 0;
    const imgs = imagePaths.map((src) => {
      const img = new window.Image();
      img.src = src;
      img.onload = img.onerror = () => {
        count++;
        if (count === imagePaths.length) setLoaded(true);
      };
      return img;
    });
    // cleanup
    return () => {
      imgs.forEach((img) => { img.onload = null; img.onerror = null; });
    };
  }, [imagePaths]);

  return loaded;
}

export default function Home() {
  const [showTop, setShowTop] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  // Εικόνες που θέλεις να περιμένεις να φορτωθούν (π.χ. hero, gallery, logo)
  const imagesToLoad = [
    "/bowling-photo.jpg",
    "/arcade.jpeg",
    "/billiard.jpg",
    "/food1.png",
    "/ping-pong1.png",
    "/george.jpeg",
    "/burger1.png",
    "/balls.jpeg",
    "/logo-arena.png",
    "/logo-team.jpg"
  ];
  const imagesLoaded = useImagesLoaded(imagesToLoad);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const splashTimeout = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(splashTimeout);
  }, []);

  // δείξε/κρύψε το "πάνω" κουμπί
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Loading: τελειώνει όταν φορτώσουν οι εικόνες ή σε 1s (ό,τι έρθει πρώτο)
  useEffect(() => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        setLoading(false);
      }
    };
    if (imagesLoaded || document.readyState === "complete") {
      finish();
    } else {
      window.addEventListener("load", finish, { once: true });
    }
    const t = setTimeout(finish, 1000);
    return () => {
      window.removeEventListener("load", finish);
      clearTimeout(t);
    };
  }, [imagesLoaded]);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  if (showSplash) {
    return (
      <Box minH="100vh" bg="black" display="flex" alignItems="center" justifyContent="center">
        <img src="/logo-arena.png" alt="Arena Bowling Center Logo" style={{ maxWidth: 320, width: '80%' }} />
      </Box>
    );
  }

  return (
    <Box position="relative" minH="100vh">
      {/* Overlay loading spinner */}
      {loading && (
        <Box position="fixed" top={0} left={0} w="100vw" h="100vh" bg="rgba(255,255,255,0.7)" zIndex={9999} display="flex" alignItems="center" justifyContent="center">
          <Spinner size="xl" thickness="4px" />
        </Box>
      )}
      <Fade in={!loading} transition={{ enter: { duration: 0.6 } }}>
        <>
          {/* HOME (Hero + Navbar πάνω του) */}
          <Box id="home" position="relative">
            <Hero />
            <Navbar />
          </Box>

          {/* ABOUT */}
          <Box id="about" py={{ base: 8, md: 12 }} scrollMarginTop={{ base: 84, md: 110 }}>
            <Container maxW="container.xl">
              <Fade in>
                <About />
              </Fade>
            </Container>
          </Box>

          {/* EVENTS */}
          <Box id="events" py={{ base: 8, md: 12 }} scrollMarginTop={{ base: 84, md: 110 }}>
            <Container maxW="container.xl">
              <Fade in>
                <Events />
              </Fade>
            </Container>
          </Box>

          {/* CONTACT */}
          <Box id="contact" py={{ base: 8, md: 12 }} scrollMarginTop={{ base: 84, md: 110 }}>
            <Container maxW="container.xl">
              <Fade in>
                <Contact />
              </Fade>
            </Container>
          </Box>

          {/* TEAM */}
          <Box id="team" py={{ base: 8, md: 12 }} scrollMarginTop={{ base: 84, md: 110 }}>
            <Container maxW="container.xl">
              <Fade in>
                <Team />
              </Fade>
            </Container>
          </Box>
          <Footer /> 

          {/* Scroll-to-top */}
          <Fade in={showTop}>
            <IconButton
              aria-label="Μετάβαση στην αρχή"
              icon={<ChevronUpIcon boxSize={6} />}
              position="fixed"
              right={{ base: 4, md: 6 }}
              bottom={{ base: 4, md: 6 }}
              rounded="full"
              shadow="lg"
              onClick={scrollToTop}
            />
          </Fade>
        </>
      </Fade>
    </Box>
  );
}
